import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { ClassCapacitySessionItemDTO } from "../api/client";

type ClassCapacitySessionsTableProps = {
  isLoading: boolean;
  data: ClassCapacitySessionItemDTO[];
};

const STATUS_BADGE: Record<
  "full" | "empty" | "normal",
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
  }
> = {
  full: { label: "ເຕັມ", variant: "default" },
  empty: { label: "ວ່າງ", variant: "secondary" },
  normal: { label: "ປານກາງ", variant: "outline" },
};

const LOCATION_KIND_LABEL: Record<"studio" | "off_site", string> = {
  studio: "ສະຕູດິໂອ",
  off_site: "ນອກສະຖານທີ່",
};

const ENGAGEMENT_LABEL: Record<"standard" | "partner_instructor_only", string> =
  {
    standard: "ມາດຕະຖານ",
    partner_instructor_only: "Partner",
  };

function placeDisplay(row: ClassCapacitySessionItemDTO): string {
  if (row.locationKind === "studio") {
    return row.roomName || "—";
  }
  return row.offSiteLabel?.trim() || "—";
}

export function ClassCapacitySessionsTable({
  isLoading,
  data,
}: ClassCapacitySessionsTableProps) {
  const colSpan = 10;
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ວັນທີ</TableHead>
            <TableHead>ເວລາ</TableHead>
            <TableHead>ປະເພດຄລາດສ໌</TableHead>
            <TableHead>ລະດັບ</TableHead>
            <TableHead>ປະເພດສະຖານທີ່</TableHead>
            <TableHead>ສະຖານທີ່/ຫ້ອງ</TableHead>
            <TableHead>ຮູບແບບ</TableHead>
            <TableHead className="text-right">ເຂົ້າຮຽນ / ຄວາມຈຸ</TableHead>
            <TableHead className="text-right">ອັດຕາ</TableHead>
            <TableHead>ສະຖານະ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={colSpan}
                className="h-24 text-center text-muted-foreground"
              >
                ກຳລັງໂຫລດ...
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={colSpan}
                className="h-24 text-center text-muted-foreground"
              >
                ບໍ່ພົບຂໍ້ມູນ
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => (
              <TableRow key={row.sessionId}>
                <TableCell className="text-sm">{row.date}</TableCell>
                <TableCell className="text-sm">{row.startTime}</TableCell>
                <TableCell className="text-sm">{row.classTypeName}</TableCell>
                <TableCell className="text-sm">{row.levelName}</TableCell>
                <TableCell className="text-sm">
                  {LOCATION_KIND_LABEL[row.locationKind]}
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-sm">
                  {placeDisplay(row)}
                </TableCell>
                <TableCell className="text-sm">
                  {ENGAGEMENT_LABEL[row.engagementKind]}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.attendance} / {row.capacity}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.utilizationPercent.toFixed(1)}%
                </TableCell>
                <TableCell>
                  <Link
                    to="/app/classes/sessions/$id"
                    params={{ id: row.sessionId }}
                  >
                    <Badge variant={STATUS_BADGE[row.status].variant}>
                      {STATUS_BADGE[row.status].label}
                    </Badge>
                  </Link>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
